import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowRight, ShoppingCart, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function Cart() {
  const { cart, removeFromCart, clearCart } = useCart();
  const nav = useNavigate();

  const empty = !cart.items.length;

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="px-6 lg:px-10 py-8">
        <Link
          to="/catalog"
          className="inline-flex items-center gap-2 font-mono-data text-xs tracking-widest hover:text-[#FF5A00]"
        >
          <ArrowLeft className="w-4 h-4" /> CONTINUE SHOPPING
        </Link>
        <div className="tag mt-6">YOUR CART</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
          {cart.items.length} {cart.items.length === 1 ? "item" : "items"}
        </h1>
      </div>

      <div className="px-6 lg:px-10 pb-16 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-3">
          {empty && (
            <div className="bg-white border-2 border-dashed border-zinc-400 p-12 text-center">
              <ShoppingCart className="w-8 h-8 mx-auto text-zinc-400" />
              <div className="font-display font-black text-2xl mt-4">Cart is empty</div>
              <p className="text-zinc-500 mt-2">Explore the catalog to add SKUs.</p>
              <button
                onClick={() => nav("/catalog")}
                data-testid="empty-cart-browse"
                className="mt-6 bg-[#FF5A00] text-white px-6 py-3 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm"
              >
                BROWSE CATALOG →
              </button>
            </div>
          )}

          {cart.items.map((it) => (
            <div
              key={it.product_id}
              data-testid={`cart-item-${it.product.sku}`}
              className="bg-white border border-zinc-900 p-4 flex gap-4"
            >
              <Link
                to={`/product/${it.product_id}`}
                className="w-24 h-24 border border-zinc-900 overflow-hidden shrink-0 bg-zinc-100"
              >
                <img
                  src={it.product.image}
                  alt={it.product.name}
                  className="w-full h-full object-cover"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="tag">{it.product.sku}</div>
                <Link
                  to={`/product/${it.product_id}`}
                  className="font-display font-bold text-lg tracking-tight leading-tight block mt-1 line-clamp-2 hover:text-[#FF5A00]"
                >
                  {it.product.name}
                </Link>
                <div className="mt-2 flex flex-wrap items-center gap-4 font-mono-data text-sm">
                  <span className="text-zinc-500">
                    {it.quantity} {it.product.unit} × ₹{it.product.price.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button
                  data-testid={`remove-item-${it.product.sku}`}
                  onClick={async () => {
                    await removeFromCart(it.product_id);
                    toast.success("Removed from cart");
                  }}
                  className="text-zinc-500 hover:text-red-600"
                  aria-label="Remove"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="font-mono-data font-bold text-lg">
                  ₹{it.line_total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="bg-white border-2 border-zinc-900 p-6 sticky top-24">
            <div className="tag">ORDER SUMMARY</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">
              Payable now
            </h2>

            <div className="mt-5 space-y-2 font-mono-data text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Subtotal</span>
                <span data-testid="cart-subtotal">
                  ₹{cart.subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">GST (18%)</span>
                <span data-testid="cart-tax">₹{cart.tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Delivery</span>
                <span data-testid="cart-delivery">
                  {cart.delivery_fee === 0 ? "FREE" : `₹${cart.delivery_fee.toFixed(2)}`}
                </span>
              </div>
            </div>
            <div className="border-t-2 border-zinc-900 border-dashed mt-4 pt-4 flex justify-between items-baseline">
              <span className="font-display font-black text-lg">TOTAL</span>
              <span data-testid="cart-total" className="font-mono-data font-bold text-2xl">
                ₹{cart.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </div>

            <button
              disabled={empty}
              onClick={() => nav("/checkout")}
              data-testid="checkout-button"
              className="mt-6 w-full bg-[#FF5A00] text-white py-4 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              PROCEED TO CHECKOUT <ArrowRight className="w-4 h-4" />
            </button>

            {!empty && (
              <button
                data-testid="clear-cart-button"
                onClick={async () => {
                  await clearCart();
                  toast.success("Cart cleared");
                }}
                className="mt-3 w-full text-xs font-mono-data text-zinc-500 hover:text-red-600 underline underline-offset-4"
              >
                Clear cart
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
